export type ThemePreference = 'system' | 'light' | 'dark'
export type ResolvedTheme = 'light' | 'dark'

/** Keep in sync with the inline theme script in public/index.html. */
export const THEME_STORAGE_KEY = 'enamelai-theme'
export const DEFAULT_THEME_PREFERENCE: ThemePreference = 'light'

const DARK_SCHEME_QUERY = '(prefers-color-scheme: dark)'

const isThemePreference = (value: unknown): value is ThemePreference =>
  value === 'system' || value === 'light' || value === 'dark'

export const getStoredThemePreference = (): ThemePreference => {
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY)
    return isThemePreference(stored) ? stored : DEFAULT_THEME_PREFERENCE
  } catch {
    return DEFAULT_THEME_PREFERENCE
  }
}

export const storeThemePreference = (preference: ThemePreference): void => {
  try {
    localStorage.setItem(THEME_STORAGE_KEY, preference)
  } catch {
    // Storage can be unavailable (private mode, quota); the theme still applies for this session.
  }
}

export const getSystemDarkQuery = (): MediaQueryList | null =>
  typeof window.matchMedia === 'function'
    ? window.matchMedia(DARK_SCHEME_QUERY)
    : null

export const resolveTheme = (preference: ThemePreference): ResolvedTheme => {
  if (preference !== 'system') return preference
  return getSystemDarkQuery()?.matches ? 'dark' : 'light'
}

export const applyDocumentTheme = (theme: ResolvedTheme): void => {
  const root = document.documentElement
  root.setAttribute('data-theme', theme)
  root.style.colorScheme = theme
}

/** `localStorage.clear()` that keeps the user's theme preference (e.g. on logout). */
export const clearLocalStorageKeepingTheme = (): void => {
  const preference = localStorage.getItem(THEME_STORAGE_KEY)
  localStorage.clear()
  if (preference !== null) {
    localStorage.setItem(THEME_STORAGE_KEY, preference)
  }
}
